import * as React from "react";
import { useMemo } from "react";
import { SmartApplyTemplate } from "../../../template/smart-apply/default-template";
import { SmartApplyR } from "../route";
import { useParams } from "react-router";
import { connectPageService } from "@graffer-inc/smart-apply";
import {
  PaymentDetailContextProps,
  PaymentDetailService,
} from "../../../../application/service/smart-apply/application-payment-detail/payment-detail-service";
import { useEffect } from "react";
import { PaymentItemTable } from "../../../organisms/smart-apply/payment/payment-detail/payment-item-table";
import { PaymentSummaryV2 } from "../../../organisms/smart-apply/payment/common/payment-summary-v2";
import { AdjustmentModal } from "../../../organisms/smart-apply/payment/adjustment-modal";
import { useModal } from "../../../../utils/custom-hooks";
import { ConfirmModal } from "../../../organisms/common/confirm-modal";
import { matchType } from "../../../../application/domain/base/base-type";
import { RBACButton } from "../../../molecules/rbac-button";
import { RefundModalV2 } from "../../../organisms/smart-apply/payment/refund-modal-v2";
import {
  formatAmount,
  InvoiceItemType,
  Payment,
  PaymentMethodUILabel,
} from "@graffer-inc/cloud-gov-core";
import { MessageAndContent } from "../../../organisms/smart-apply/application-detail/message-and-content";
import { PaymentStatusTag } from "../../../organisms/smart-apply/payment/payment-status";
import { PayeasyDetail } from "../../../organisms/smart-apply/payment/payeasy-detail/payeasy-detail";
import { MessageDialog } from "@graffer-inc/console-design-kit";
import { useAuthContextV2 } from "../../../../auth-state";
import dayjs from "../../../../utils/date-utils";
import { PaymentStatusTag as PaymentOperationTag } from "../../../molecules/tags/payment-status-tag";
import { SmartApplyMenuList } from "../../../organisms/smart-apply/menu-list";

export const ApplicationPaymentDetail: React.FC<PaymentDetailContextProps> = ({
  loadingStates,
  application,
  paymentOperations,
  procedure,
  addedEvent,
  refund,
  paymentDetail,
  withdraw,
  onPageMount,
  adjustExceedingOrDeficiency,
}) => {
  const refundModal = useModal();
  const adjustmentModel = useModal();
  const withdrawnConfirmModal = useModal();
  const { id } = useParams<{ id: string }>();
  const { index } = useParams<{ index: string }>();
  const idx = parseInt(index, 10);
  const auth = useAuthContextV2();

  const isInitialized = !!(application && procedure);
  const isAcceptMultiMethodsOrg: boolean = useMemo(() => {
    if (auth) {
      return auth.auth.acceptedPaymentMethods.length > 1;
    }
    return false;
  }, [auth]);
  const latestPayeasyDetail = useMemo(() => {
    const payeasyDetail = paymentDetail.data?.payeasyDetail;
    return payeasyDetail && payeasyDetail.length > 0
      ? payeasyDetail[payeasyDetail.length - 1]
      : null;
  }, [paymentDetail]);

  useEffect(() => {
    onPageMount(id, idx);
  }, [onPageMount, id, idx]);

  if (!isInitialized) {
    return null;
  }

  const targetPayment = new Payment(application.getPaymentsV2()[idx]);
  const title = `${dayjs(targetPayment.createdAt).format(
    "YYYY年MM月DD日 HH:mm",
  )} の支払い`;
  const paidPaymentMethods =
    paymentDetail && !!paymentDetail.data && paymentDetail.data.paymentMethods
      ? paymentDetail.data.paymentMethods.filter((p) => !!p)
      : [];

  const isRefundDisabled = !paymentDetail.data?.isRefundablePaymentMethod;
  const isCancelDisabled =
    latestPayeasyDetail !== null && !latestPayeasyDetail.isExpiredWithNoPayment;

  return (
    <SmartApplyTemplate
      breadcrumbs={[
        { to: SmartApplyR.Applications.link({}), title: "申請一覧" },
        {
          to: SmartApplyR.ApplicationDetail.link({ id: application?.id || "" }),
          title: application?.formattedId || "",
        },
        {
          to: SmartApplyR.ApplicationPaymentDetail.link({
            id: application?.id || "",
            index: idx,
          }),
          title: title,
        },
      ]}
      menuList={<SmartApplyMenuList />}
    >
      {isInitialized && (
        <>
          <div
            style={{
              maxWidth: "800px",
            }}
          >
            <h1 className="title">
              <i className="fa fa-file-invoice mr-1" /> {title}
            </h1>
            <div className="columns is-marginless is-paddingless is-mobile">
              <div className="column pl-0">
                <h2 className="title is-size-4 mb-2">支払いの詳細</h2>
              </div>
              <div className="column is-narrow pr-0">
                <div className="has-text-right mb-2">
                  {!application?.isDone() &&
                    matchType(targetPayment.status, {
                      requested: (
                        <RBACButton
                          target={procedure}
                          requirePermissions="applications.post_payment_withdrawn"
                          onClick={withdrawnConfirmModal.open}
                          buttonTypes={"secondary"}
                          buttonText={"キャンセルする"}
                        />
                      ),
                      paid: (
                        <RBACButton
                          target={procedure}
                          requirePermissions="applications.payment_refund"
                          onClick={refundModal.open}
                          buttonTypes={"secondary"}
                          buttonText={"返金する"}
                        />
                      ),
                      partial_refunded: (
                        <RBACButton
                          target={procedure}
                          requirePermissions="applications.payment_refund"
                          onClick={refundModal.open}
                          buttonTypes={"secondary"}
                          buttonText={"返金する"}
                        />
                      ),
                      refunded: null,
                      cancelled: null,
                      deficient_payment: (
                        <RBACButton
                          target={procedure}
                          requirePermissions="applications.payment_adjustment"
                          onClick={adjustmentModel.open}
                          buttonTypes="secondary"
                          buttonText="入金不足を解消する"
                        />
                      ),
                      exceeding_payment: (
                        <RBACButton
                          target={procedure}
                          requirePermissions="applications.payment_adjustment"
                          onClick={adjustmentModel.open}
                          buttonTypes="secondary"
                          buttonText="過入金を解消する"
                        />
                      ),
                    })}
                </div>
              </div>
            </div>
            <div>
              <div>
                <PaymentStatusTag status={targetPayment.status} />
              </div>
              <h3 className="title is-size-5 has-text-weight-light mb-4 mt-4">
                明細
              </h3>
              {paymentOperations.length === 0 ? (
                // まだ支払いが無いうちはpaymentOperationsが無いため、paymentに紐付いたinvoiceを表示する
                <PaymentItemTable invoice={targetPayment.invoice} />
              ) : (
                <PaymentItemTable
                  isShowDate={true}
                  invoice={{
                    items: paymentOperations.reduce(
                      (pv: InvoiceItemType[], cv) => [
                        ...pv,
                        ...cv.items
                          .filter(
                            (v) =>
                              !(
                                cv.paymentTransactionType !== "CHARGE" &&
                                v.amount === 0
                              ),
                          )
                          .map((v, idx) => ({
                            ...v,
                            createdAt: cv.createdAt,
                            operationIndex: idx,
                            amount: matchType(cv.paymentTransactionType, {
                              CHARGE: matchType(v.type, {
                                "": v.amount,
                                deficiency: -v.amount,
                                deficiency_adjuster: v.amount,
                                exceeding: v.amount,
                                exceeding_adjuster: -v.amount,
                              }),
                              ADJUST_CHARGE: v.amount,
                              ADJUST_REFUND: -v.amount,
                              PARTIAL_REFUND: -v.amount,
                              REFUND: -v.amount,
                            }),
                          })),
                      ],
                      [],
                    ),
                  }}
                />
              )}
              <div>
                <PaymentSummaryV2
                  // paymentDetail.dataがないときは、paymentIdがnullの場合、つまり決済要求中あるいは決済要求がキャンセルされた場合に限る。
                  // その時は、決済要求の詳細をtargetPaymentが保持するので表示する情報を切り替える
                  payment={
                    paymentDetail.data ? paymentDetail.data : targetPayment
                  }
                />
              </div>
            </div>
            {addedEvent && (
              <div className="mt-6">
                <MessageAndContent data={addedEvent.data} />
              </div>
            )}
            <div className="mt-6">
              <h2 className="title is-size-4 mt-6">支払い方法</h2>
              <ul
                className="ml-5"
                style={{
                  listStyle: "square",
                }}
              >
                {targetPayment.acceptedPaymentMethods.map((m) => (
                  <li key={m}>{PaymentMethodUILabel[m]}</li>
                ))}
              </ul>
              {/* TODO: PaymentSummaryモデルを修正後、当該コンポーネントの表示ロジックを修正する */}
              {latestPayeasyDetail && !latestPayeasyDetail.isPaid && (
                <div className="mt-2">
                  <div className="notification is-light">
                    <p>申請者によりペイジー支払い情報が発行されています。</p>
                    <div className="has-background-white has-text-black mt-3">
                      <PayeasyDetail
                        payeasyDetail={{
                          invoiceNo: latestPayeasyDetail.invoiceNo,
                          billDate: latestPayeasyDetail.billDate,
                          skNo: latestPayeasyDetail.skNo,
                          customerNum: latestPayeasyDetail.customerNum,
                          confirmationNum: latestPayeasyDetail.confirmationNum,
                          isExpiredWithNoPayment:
                            latestPayeasyDetail.isExpiredWithNoPayment,
                        }}
                      />
                    </div>
                    {latestPayeasyDetail.isExpiredWithNoPayment && (
                      <div className="mt-5">
                        <div className="notification is-warning is-light">
                          こちらのペイジー支払いはお支払い期限切れとなり、ご利用できません。
                          <br />
                          申請者にてもう一度を支払い方法を設定することで支払いが可能です。申請者からの設定を待つか、再設定の連絡をしてください。
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <h2 className="title is-size-4 mt-6">決済履歴</h2>
            <table className="table is-fullwidth c-border">
              <thead>
                <tr>
                  <th>日付</th>
                  <th className="has-text-left">種別</th>
                  {isAcceptMultiMethodsOrg && (
                    <th className="has-text-left">支払い手段</th>
                  )}
                  <th className="has-text-right">金額</th>
                </tr>
              </thead>
              <tbody>
                {paymentOperations.length === 0 && (
                  <tr key={idx}>
                    <td colSpan={isAcceptMultiMethodsOrg ? 5 : 4}>
                      まだ決済が行われていないため履歴がありません。
                    </td>
                  </tr>
                )}
                {paymentOperations.map((v, idx) => (
                  <tr key={idx}>
                    <td>{dayjs(v.createdAt).format("YYYY年MM月DD日 HH:mm")}</td>
                    <td>
                      <PaymentOperationTag type={v.paymentTransactionType} />
                    </td>
                    {isAcceptMultiMethodsOrg && (
                      <td>
                        {v.paymentTransactionType === "CHARGE" &&
                        paidPaymentMethods.length > 0
                          ? paidPaymentMethods
                              .map((pm) => PaymentMethodUILabel[pm])
                              .join(",") + "支払い"
                          : ""}
                      </td>
                    )}
                    <td className="has-text-right">
                      <p>{formatAmount(v.amount)}円</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {withdrawnConfirmModal.isActive &&
            matchType(isCancelDisabled ? "disabled" : "enabled", {
              disabled: (
                <MessageDialog
                  title="キャンセルができません"
                  contents={
                    <p>
                      申請者がペイジー支払いを発行しているため、キャンセルできません。
                      <br />
                      支払期限を過ぎた後にキャンセルするか、申請者に連絡をしてください。
                    </p>
                  }
                  cancelText="閉じる"
                  onCancel={withdrawnConfirmModal.close}
                />
              ),
              enabled: (
                <ConfirmModal
                  title={"決済依頼のキャンセルの確認"}
                  body={`${targetPayment.amount}円の決済依頼をキャンセルします。よろしいですか？`}
                  isSuccessLoading={loadingStates.isWithdrawing}
                  successButtonText={"キャンセルする"}
                  onSuccess={async () => {
                    if (await withdraw(idx)) {
                      withdrawnConfirmModal.close();
                    }
                  }}
                  isActive={withdrawnConfirmModal.isActive}
                  onClose={withdrawnConfirmModal.close}
                />
              ),
            })}
          {refundModal.isActive &&
            paymentDetail.data &&
            matchType(isRefundDisabled ? "disabled" : "enabled", {
              disabled: (
                <MessageDialog
                  title="返金ができません"
                  contents={
                    <p>
                      ペイジー支払いはシステム上で返金することができません。
                      申請者と連絡をとり、システム外での返金対応をしてください。
                    </p>
                  }
                  cancelText="閉じる"
                  onCancel={refundModal.close}
                />
              ),
              enabled: (
                <RefundModalV2
                  summary={paymentDetail.data}
                  isSubmitting={loadingStates.isRefunding}
                  onClose={refundModal.close}
                  onOk={async (req, reason) => {
                    if (await refund(req, reason)) {
                      refundModal.close();
                    }
                  }}
                />
              ),
            })}
          {adjustmentModel.isActive && paymentDetail.data && (
            <AdjustmentModal
              targetPayment={targetPayment}
              isSubmitting={false}
              onClose={adjustmentModel.close}
              onResolve={async () => {
                if (await adjustExceedingOrDeficiency(idx)) {
                  adjustmentModel.close();
                }
              }}
            />
          )}
        </>
      )}
    </SmartApplyTemplate>
  );
};

export const PaymentDetailContainer = connectPageService(
  ApplicationPaymentDetail,
  () => new PaymentDetailService(),
);
